import {AConst} from '../core/a-const.enum';
import {MetaField} from '../core/definitions/meta-field';
import {SearchContainer} from '../core/definitions/search-container';

export function isChildFieldOrder(order: string | null | undefined): boolean {
  return !!order?.includes('childfield(') && !!order?.includes('$parent_fq');
}

export function getOrderFieldName(fieldName: string, fieldInfo?: MetaField): string {
  let orderFieldName = fieldName;
  if (fieldInfo && fieldInfo.input_type === AConst.MAP_ID) {
    orderFieldName = fieldName + '_value';
  }
  if (fieldInfo?.path && !orderFieldName.includes('.')) {
    orderFieldName = `${fieldInfo.path}.${orderFieldName}`;
  }
  return orderFieldName;
}

export function getParentFilterFromSearchContainer(searchContainer: SearchContainer): string | undefined {
  const filters = searchContainer.currentPathView?.search_view?.filters;
  if (!filters) {
    return undefined;
  }
  const parentMetaTypeFilter = filters['parent_meta_type'];
  const metaTypeFilter = filters['meta_type'];
  const objectTypeFilter = filters['object_type'];
  if (parentMetaTypeFilter) {
    const filterValue = Array.isArray(parentMetaTypeFilter) ? parentMetaTypeFilter[0] : parentMetaTypeFilter;
    return `parent_meta_type:${filterValue}`;
  }
  if (metaTypeFilter) {
    const filterValue = Array.isArray(metaTypeFilter) ? metaTypeFilter[0] : metaTypeFilter;
    return `meta_type:${filterValue}`;
  }
  if (objectTypeFilter) {
    const filterValue = Array.isArray(objectTypeFilter) ? objectTypeFilter[0] : objectTypeFilter;
    return `object_type:${filterValue}`;
  }
  return undefined;
}

export function getChildOrderFieldName(
  searchContainer: SearchContainer,
  childObjectType: string,
  orderFieldName: string
): [string, string | null] {
  const parentFilter = getParentFilterFromSearchContainer(searchContainer);
  if (parentFilter) {
    const parentFq = `{!parent which=${parentFilter}}` +
      `object_type:${childObjectType}`;
    const childFieldName = `childfield(${orderFieldName},$parent_fq)`;
    return [childFieldName, parentFq];
  }
  console.warn('Could not determine parent filter for ordering on child field ' +
    orderFieldName + ' with parent object type ' + childObjectType);
  return [orderFieldName, null];
}

export function ensureChildOrderParentQuery(searchContainer: SearchContainer, fields: MetaField[]): void {
  const order = searchContainer.getOrder();
  if (!isChildFieldOrder(order) || searchContainer.orderParentQuery) {
    return;
  }
  const match = order.match(/childfield\(([^,]+),\s*\$parent_fq\)/);
  if (!match) {
    return;
  }
  const childOrderFieldName = match[1].trim();
  const childObjectType = findChildObjectType(fields, childOrderFieldName);
  if (!childObjectType?.includes('Item')) {
    return;
  }
  const [, parentQuery] = getChildOrderFieldName(searchContainer, childObjectType, childOrderFieldName);
  if (parentQuery) {
    searchContainer.orderParentQuery = parentQuery;
  }
}

function findChildObjectType(fields: MetaField[], childOrderFieldName: string): string | undefined {
  const exact = (fields || []).find(field =>
    getOrderFieldName(field.field_name || field.name, field) === childOrderFieldName);
  const fromExact = exact?.model_type || exact?.parent_object_type;
  if (fromExact?.includes('Item')) {
    return fromExact;
  }
  const samePath = (fields || []).find(field => {
    const type = field.model_type || field.parent_object_type;
    return type?.includes('Item') && field.path && childOrderFieldName.startsWith(`${field.path}.`);
  });
  return samePath?.model_type || samePath?.parent_object_type;
}
